import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchHubspotPropertiesIfNeeded,
  getHubspotProperties,
  getHubspotPropertiesLoading,
} from 'client/modules/Settings/HubspotPropertiesReducer';
import { getToken } from 'client/modules/Auth/AuthReducer';

const HubspotPropertyAutocomplete = ({
  value,
  onChange,
  defaultLabel,
  className,
  error,
  helperText,
}) => {
  const dispatch = useDispatch();
  const allOptions = useSelector(getHubspotProperties);
  const propertyLoading = useSelector(getHubspotPropertiesLoading);
  const token = useSelector(getToken);
  const [propertyInput, setPropertyInput] = useState('');

  useEffect(() => {
    if (token) {
      dispatch(fetchHubspotPropertiesIfNeeded(token));
    }
  }, [dispatch, token]);

  // Filter options on the client
  const filteredOptions = propertyInput
    ? allOptions.filter(o => {
        const words = propertyInput.toLowerCase().split(/\s+/).filter(Boolean);
        const label = (o.label || '').toLowerCase();
        const name = (o.name || '').toLowerCase();
        return words.every(
          word => label.includes(word) || name.includes(word) || o.groupLabel.includes(word),
        );
      })
    : allOptions;

  return (
    <Autocomplete
      freeSolo
      groupBy={option => option.groupLabel}
      filterOptions={x => x}
      options={filteredOptions}
      loading={propertyLoading}
      getOptionLabel={option =>
        option.label ? `${option.label} (${option.name})` : option.name || ''
      }
      isOptionEqualToValue={(option, value) => option.name === value.name}
      value={value ? allOptions.find(o => o.name === value) || { name: value, label: '' } : null}
      onInputChange={(e, newInput, reason) => {
        if (reason === 'input') setPropertyInput(newInput);
      }}
      onChange={(event, newValue) => {
        onChange(event, newValue || null);
      }}
      className={className}
      renderInput={params => (
        <TextField
          {...params}
          variant="standard"
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            disableUnderline: true,
          }}
          placeholder={defaultLabel}
          aria-label="Hubspot Property Name"
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.name}>
          <Box>
            <strong>{option.label}</strong> <span style={{ color: '#888' }}>({option.name})</span>
          </Box>
        </li>
      )}
    />
  );
};

HubspotPropertyAutocomplete.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  defaultLabel: PropTypes.string,
  className: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.node,
};

HubspotPropertyAutocomplete.defaultProps = {
  value: '',
  defaultLabel: '',
  className: '',
  error: false,
  helperText: null,
};

export default HubspotPropertyAutocomplete;
